<template>
  <div>
    <!-- Create Adjustment Modal -->
    <div class="modal fade adjustment-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" role="dialog" aria-labelledby="adjustmentModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title fw-bold" id="adjustmentModalLabel">
              <i class="mdi mdi-tune-vertical me-2"></i>Balance Adjustment
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body p-0" v-if="plot">
            <!-- Plot Information Card -->
            <div class="bg-light border-bottom p-4">
              <div class="row">
                <div class="col-md-6">
                  <div class="property-info">
                    <h6 class="fw-bold text-warning mb-3">
                      <i class="mdi mdi-store me-2"></i>Plot Details
                    </h6>
                    <div class="info-row mb-2">
                      <span class="text-muted">Plot Number:</span>
                      <span class="fw-bold ms-2">{{ plot.plotNumber || 'N/A' }}</span>
                    </div>
                    <div class="info-row mb-2">
                      <span class="text-muted">Zone/Market:</span>
                      <span class="fw-bold ms-2">{{ plot.zone || 'N/A' }}</span>
                    </div>
                    <div class="info-row mb-2">
                      <span class="text-muted">Owner:</span>
                      <span class="fw-bold ms-2">{{ plot.ownerNames || 'N/A' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="text-muted">Location:</span>
                      <span class="fw-bold ms-2">{{ plot.subCountyName || '' }} {{ plot.wardName ? '- ' + plot.wardName : '' }}</span>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="financial-info">
                    <h6 class="fw-bold text-warning mb-3">
                      <i class="mdi mdi-currency-usd me-2"></i>Current Balances
                    </h6>
                    <div class="info-row mb-2">
                      <span class="text-muted">Annual Rent:</span>
                      <span class="fw-bold text-success ms-2">KES {{ numberFormat(plot.annualRent) }}</span>
                    </div>
                    <div class="info-row mb-2">
                      <span class="text-muted">Arrears B/F:</span>
                      <span class="fw-bold text-danger ms-2">KES {{ numberFormat(plot.arrearsForward) }}</span>
                    </div>
                    <div class="info-row mb-2">
                      <span class="text-muted">Accumulated Penalty:</span>
                      <span class="fw-bold text-warning ms-2">KES {{ numberFormat(plot.accumulatedPenalty) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="text-muted">Current Balance:</span>
                      <span class="fw-bold ms-2" :class="balanceClass">KES {{ numberFormat(plot.currentBalance) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Adjustment Form -->
            <div class="p-4">
              <form @submit.prevent="submitAdjustment">
                <div class="row">
                  <!-- Adjustment Type -->
                  <div class="col-12 mb-4">
                    <label class="form-label fw-semibold">
                      Adjustment Type <span class="text-danger">*</span>
                    </label>
                    <div class="d-flex gap-3">
                      <div class="form-check form-check-inline adjustment-type-card" :class="{ 'selected': adjustmentType === 'Credit' }">
                        <input 
                            v-model="adjustmentType" 
                            class="form-check-input" 
                            type="radio" 
                            name="adjustmentType" 
                            id="typeCredit" 
                            value="Credit"
                            @change="onAdjustmentTypeChange">
                        <label class="form-check-label" for="typeCredit">
                          <i class="mdi mdi-arrow-down-bold-circle text-success me-1" style="font-size: 24px;"></i>
                          <strong class="text-success">Credit (Reduce Balance)</strong>
                          <small class="d-block text-muted">Reduces customer's outstanding balance</small>
                        </label>
                      </div>
                      <div class="form-check form-check-inline adjustment-type-card" :class="{ 'selected': adjustmentType === 'Debit' }">
                        <input 
                            v-model="adjustmentType" 
                            class="form-check-input" 
                            type="radio" 
                            name="adjustmentType" 
                            id="typeDebit" 
                            value="Debit"
                            @change="onAdjustmentTypeChange">
                        <label class="form-check-label" for="typeDebit">
                          <i class="mdi mdi-arrow-up-bold-circle text-danger me-1" style="font-size: 24px;"></i>
                          <strong class="text-danger">Debit (Increase Balance)</strong>
                          <small class="d-block text-muted">Increases customer's outstanding balance</small>
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Adjustment Category -->
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">
                        Adjustment Category <span class="text-danger">*</span>
                      </label>
                      <select 
                          v-model="adjustmentCategory" 
                          class="form-select"
                          :class="{ 'is-invalid': errors.category }"
                          :disabled="!adjustmentType"
                          required>
                        <option value="">Select Category</option>
                        <option v-for="cat in filteredCategories" :key="cat.id" :value="cat.categoryCode">
                          {{ cat.categoryName }}
                        </option>
                      </select>
                      <div v-if="errors.category" class="invalid-feedback">{{ errors.category }}</div>
                      <small v-if="selectedCategory?.description" class="text-muted">
                        {{ selectedCategory.description }}
                      </small>
                    </div>
                  </div>

                  <!-- Amount -->
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">
                        Amount (KES) <span class="text-danger">*</span>
                      </label>
                      <div class="input-group">
                        <span class="input-group-text">KES</span>
                        <input 
                            v-model.number="amount" 
                            type="number" 
                            step="0.01" 
                            min="0.01"
                            class="form-control"
                            :class="{ 'is-invalid': errors.amount }"
                            placeholder="Enter amount"
                            required>
                      </div>
                      <div v-if="errors.amount" class="invalid-feedback d-block">{{ errors.amount }}</div>
                    </div>
                  </div>

                  <!-- Quick Amount Buttons -->
                  <div class="col-12 mb-3" v-if="adjustmentType === 'Credit'">
                    <small class="text-muted me-2">Quick amounts:</small>
                    <button 
                        v-if="plot.currentBalance > 0" 
                        type="button" 
                        class="btn btn-outline-success btn-sm me-2"
                        @click="setAmount(plot.currentBalance)">
                      Full Balance ({{ numberFormat(plot.currentBalance) }})
                    </button>
                    <button 
                        v-if="plot.accumulatedPenalty > 0" 
                        type="button" 
                        class="btn btn-outline-warning btn-sm me-2"
                        @click="setAmount(plot.accumulatedPenalty)">
                      All Penalties ({{ numberFormat(plot.accumulatedPenalty) }})
                    </button>
                    <button 
                        v-if="plot.arrearsForward > 0" 
                        type="button" 
                        class="btn btn-outline-danger btn-sm"
                        @click="setAmount(plot.arrearsForward)">
                      All Arrears ({{ numberFormat(plot.arrearsForward) }})
                    </button>
                  </div>

                  <!-- Fiscal Year -->
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Fiscal Year</label>
                      <select v-model="fiscalYear" class="form-select">
                        <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                        <option :value="currentYear">{{ currentYear }}</option>
                        <option :value="currentYear + 1">{{ currentYear + 1 }}</option>
                      </select>
                    </div>
                  </div>

                  <!-- Approval Level Preview -->
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">Required Approval Level</label>
                      <div class="approval-level-badge">
                        <span v-if="requiredApprovalLevel === 1" class="badge bg-success px-3 py-2">
                          <i class="mdi mdi-account-check me-1"></i>Level 1 - Officer
                        </span>
                        <span v-else-if="requiredApprovalLevel === 2" class="badge bg-warning px-3 py-2">
                          <i class="mdi mdi-account-supervisor me-1"></i>Level 2 - Supervisor
                        </span>
                        <span v-else-if="requiredApprovalLevel === 3" class="badge bg-danger px-3 py-2">
                          <i class="mdi mdi-account-tie me-1"></i>Level 3 - Manager
                        </span>
                        <span v-else class="badge bg-secondary px-3 py-2">
                          <i class="mdi mdi-help-circle me-1"></i>Enter amount to see
                        </span>
                      </div>
                      <small class="text-muted d-block mt-1">Based on amount thresholds</small>
                    </div>
                  </div>

                  <!-- Reason -->
                  <div class="col-12">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">
                        Reason / Justification <span class="text-danger">*</span>
                      </label>
                      <textarea 
                          v-model="reason" 
                          class="form-control"
                          :class="{ 'is-invalid': errors.reason }"
                          rows="3"
                          placeholder="Provide detailed reason for this adjustment..."
                          required></textarea>
                      <div v-if="errors.reason" class="invalid-feedback">{{ errors.reason }}</div>
                      <small class="text-muted">Minimum 20 characters required</small>
                    </div>
                  </div>

                  <!-- Supporting Documents Reference -->
                  <div class="col-12" v-if="selectedCategory?.requiresDocument">
                    <div class="form-group mb-3">
                      <label class="form-label fw-semibold">
                        Supporting Document Reference <span class="text-danger">*</span>
                      </label>
                      <input 
                          v-model="supportingDocuments" 
                          type="text" 
                          class="form-control"
                          placeholder="Enter document reference number(s)">
                      <small class="text-muted">This category requires supporting documentation</small>
                    </div>
                  </div>

                  <!-- Adjustment Preview -->
                  <div class="col-12" v-if="amount > 0 && adjustmentType">
                    <div class="card mb-3" :class="adjustmentType === 'Credit' ? 'border-success' : 'border-danger'">
                      <div class="card-header" :class="adjustmentType === 'Credit' ? 'bg-success' : 'bg-danger'" style="color: white;">
                        <h6 class="mb-0" style="color: white;">
                          <i class="mdi me-2" :class="adjustmentType === 'Credit' ? 'mdi-arrow-down-bold-circle' : 'mdi-arrow-up-bold-circle'"></i>
                          Adjustment Preview
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="row text-center">
                          <div class="col-4">
                            <div class="text-muted small">Current Balance</div>
                            <div class="fw-bold h5 text-danger">KES {{ numberFormat(plot.currentBalance) }}</div>
                          </div>
                          <div class="col-4">
                            <div class="text-muted small">Adjustment</div>
                            <div class="fw-bold h5" :class="adjustmentType === 'Credit' ? 'text-success' : 'text-danger'">
                              {{ adjustmentType === 'Credit' ? '-' : '+' }} KES {{ numberFormat(amount) }}
                            </div>
                          </div>
                          <div class="col-4">
                            <div class="text-muted small">New Balance</div>
                            <div class="fw-bold h5" :class="newBalance > 0 ? 'text-danger' : 'text-success'">
                              KES {{ numberFormat(newBalance) }}
                            </div>
                          </div>
                        </div>

                        <div class="alert mt-3 mb-0" :class="adjustmentType === 'Credit' ? 'alert-success' : 'alert-warning'">
                          <small>
                            <i class="mdi mdi-information-outline me-1"></i>
                            <template v-if="adjustmentType === 'Credit'">
                              This credit adjustment will <strong>reduce</strong> the customer's balance by KES {{ numberFormat(amount) }}.
                            </template>
                            <template v-else>
                              This debit adjustment will <strong>increase</strong> the customer's balance by KES {{ numberFormat(amount) }}.
                            </template>
                            <br>
                            <strong>Approval Required:</strong> {{ approvalLevelText }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-outline-secondary flex-fill" data-bs-dismiss="modal">
                    <i class="mdi mdi-close me-1"></i>Cancel
                  </button>
                  <button type="button" class="btn btn-info flex-fill" @click="showHistory = true" v-if="hasAdjustmentHistory">
                    <i class="mdi mdi-history me-1"></i>View History ({{ adjustmentHistory.length }})
                  </button>
                  <button 
                      type="submit" 
                      class="btn flex-fill"
                      :class="adjustmentType === 'Credit' ? 'btn-success' : 'btn-danger'"
                      :disabled="!isFormValid || isSubmitting">
                    <span v-if="isSubmitting">
                      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                      Submitting...
                    </span>
                    <span v-else>
                      <i class="mdi mdi-check-circle me-1"></i>Submit for Approval
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Adjustment History Modal -->
    <div class="modal fade adjustment-history-modal" tabindex="-1" role="dialog" aria-hidden="true" v-if="showHistory">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-history me-2"></i>
              Adjustment History - {{ plot?.plotNumber }}
            </h5>
            <button type="button" class="btn-close btn-close-white" @click="showHistory = false"></button>
          </div>
          <div class="modal-body">
            <!-- Summary Cards -->
            <div class="row mb-4" v-if="historySummary">
              <div class="col-md-3">
                <div class="card border-primary">
                  <div class="card-body text-center">
                    <div class="h4 text-primary">{{ historySummary.totalAdjustments || 0 }}</div>
                    <div class="text-muted small">Total Adjustments</div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card border-success">
                  <div class="card-body text-center">
                    <div class="h4 text-success">KES {{ numberFormat(historySummary.totalCreditsApplied || 0) }}</div>
                    <div class="text-muted small">Credits Applied</div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card border-danger">
                  <div class="card-body text-center">
                    <div class="h4 text-danger">KES {{ numberFormat(historySummary.totalDebitsApplied || 0) }}</div>
                    <div class="text-muted small">Debits Applied</div>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card border-warning">
                  <div class="card-body text-center">
                    <div class="h4 text-warning">{{ historySummary.pending || 0 }}</div>
                    <div class="text-muted small">Pending Approval</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- History Table -->
            <div class="table-responsive">
              <table class="table table-striped table-hover">
                <thead class="table-dark">
                  <tr>
                    <th>Reference</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Before → After</th>
                    <th>Status</th>
                    <th>Requested By</th>
                    <th>Approval Trail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="adj in adjustmentHistory" :key="adj.id">
                    <td>
                      <strong class="text-primary">{{ adj.adjustmentRef }}</strong>
                    </td>
                    <td>{{ formatDate(adj.requestedDate) }}</td>
                    <td>
                      <span class="badge" :class="adj.adjustmentType === 'Credit' ? 'bg-success' : 'bg-danger'">
                        {{ adj.adjustmentType }}
                      </span>
                    </td>
                    <td>{{ adj.adjustmentCategory }}</td>
                    <td class="text-end fw-bold">
                      <span :class="adj.adjustmentType === 'Credit' ? 'text-success' : 'text-danger'">
                        {{ adj.adjustmentType === 'Credit' ? '-' : '+' }} KES {{ numberFormat(adj.amount) }}
                      </span>
                    </td>
                    <td class="text-center">
                      {{ numberFormat(adj.balanceBefore) }} → {{ numberFormat(adj.balanceAfter) }}
                    </td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(adj.status)">
                        {{ adj.status }}
                      </span>
                    </td>
                    <td>{{ adj.requestedBy }}</td>
                    <td>
                      <small class="text-muted">{{ adj.approvalTrail || '-' }}</small>
                    </td>
                  </tr>
                  <tr v-if="!adjustmentHistory || adjustmentHistory.length === 0">
                    <td colspan="9" class="text-center text-muted py-4">
                      No adjustment history found for this plot
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showHistory = false">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/* eslint-disable */

import { execute, plotRent } from "@/api";

export default {
  name: 'PlotAdjustmentModal',
  props: {
    plot: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['adjustment-created', 'refresh-plot'],
  data() {
    return {
      // Form fields
      adjustmentType: '',
      adjustmentCategory: '',
      amount: 0,
      reason: '',
      fiscalYear: new Date().getFullYear(),
      supportingDocuments: '',
      
      // Data
      categories: [],
      thresholds: [],
      adjustmentHistory: [],
      historySummary: null,
      
      // UI State
      isSubmitting: false,
      showHistory: false,
      errors: {},
      currentYear: new Date().getFullYear()
    }
  },
  computed: {
    balanceClass() {
      if (!this.plot) return 'text-muted';
      const balance = parseFloat(this.plot.currentBalance) || 0;
      return balance > 0 ? 'text-danger' : 'text-success';
    },

    filteredCategories() {
      if (!this.adjustmentType) return this.categories;
      return this.categories.filter(cat => 
        cat.adjustmentType === this.adjustmentType || cat.adjustmentType === 'Both'
      );
    },

    selectedCategory() {
      return this.categories.find(cat => cat.categoryCode === this.adjustmentCategory);
    },

    newBalance() {
      if (!this.plot) return 0;
      const currentBalance = parseFloat(this.plot.currentBalance) || 0;
      const adjustmentAmount = parseFloat(this.amount) || 0;
      
      if (this.adjustmentType === 'Credit') {
        return currentBalance - adjustmentAmount;
      } else {
        return currentBalance + adjustmentAmount;
      }
    },

    requiredApprovalLevel() {
      if (!this.amount || this.amount <= 0 || !this.thresholds.length) return 0;
      
      const amt = parseFloat(this.amount);
      for (let i = this.thresholds.length - 1; i >= 0; i--) {
        const threshold = this.thresholds[i];
        const min = parseFloat(threshold.minAmount);
        const max = threshold.maxAmount ? parseFloat(threshold.maxAmount) : Infinity;
        
        if (amt >= min && amt <= max) {
          return parseInt(threshold.approvalLevel);
        }
      }
      return 1;
    },

    approvalLevelText() {
      switch (this.requiredApprovalLevel) {
        case 1: return 'Level 1 - Officer Approval';
        case 2: return 'Level 2 - Supervisor Approval';
        case 3: return 'Level 3 - Manager Approval';
        default: return 'To be determined';
      }
    },

    hasAdjustmentHistory() {
      return this.adjustmentHistory && this.adjustmentHistory.length > 0;
    },

    isFormValid() {
      return this.plot &&
          this.adjustmentType &&
          this.adjustmentCategory &&
          this.amount > 0 &&
          this.reason && this.reason.length >= 20 &&
          (!this.selectedCategory?.requiresDocument || this.supportingDocuments) &&
          Object.keys(this.errors).length === 0;
    }
  },
  watch: {
    plot: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadCategories();
          this.loadThresholds();
          this.loadHistory();
          this.resetForm();
        }
      },
      immediate: true,
      deep: true
    },
    amount(newVal) {
      this.validateAmount(newVal);
    },
    reason(newVal) {
      this.validateReason(newVal);
    }
  },
  methods: {
    loadCategories() {
      const data = new FormData();
      data.append("function", "getAdjustmentCategories");

      execute(data, plotRent)
        .then((res) => {
          if (res.data.success) {
            this.categories = res.data.data.categories || [];
          }
        })
        .catch((e) => {
          console.error("Error loading categories:", e);
        });
    },

    loadThresholds() {
      const data = new FormData();
      data.append("function", "getAdjustmentThresholds");

      execute(data, plotRent)
        .then((res) => {
          if (res.data.success) {
            this.thresholds = res.data.data.thresholds || [];
          }
        })
        .catch((e) => {
          console.error("Error loading thresholds:", e);
        });
    },

    loadHistory() {
      if (!this.plot || !this.plot.id) return;

      const data = new FormData();
      data.append("function", "getPlotAdjustmentHistory");
      data.append("plotID", this.plot.id);

      execute(data, plotRent)
        .then((res) => {
          if (res.data.success) {
            this.adjustmentHistory = res.data.data.adjustments || [];
            this.historySummary = res.data.data.summary || null;
          }
        })
        .catch((e) => {
          console.error("Error loading history:", e);
        });
    },

    onAdjustmentTypeChange() {
      // Reset category when type changes
      this.adjustmentCategory = '';
    },

    setAmount(value) {
      this.amount = parseFloat(value) || 0;
    },

    validateAmount(amount) {
      delete this.errors.amount;

      if (!amount || amount <= 0) {
        this.errors.amount = 'Amount must be greater than 0';
      } else if (amount > 10000000) {
        this.errors.amount = 'Amount seems too large. Please verify.';
      } else if (this.adjustmentType === 'Credit' && amount > parseFloat(this.plot?.currentBalance || 0)) {
        // Warning but not error for credit exceeding balance
        console.warn('Credit amount exceeds current balance');
      }
    },

    validateReason(reason) {
      delete this.errors.reason;

      if (!reason || reason.trim().length < 20) {
        this.errors.reason = 'Reason must be at least 20 characters';
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.adjustmentType) {
        this.errors.type = 'Please select adjustment type';
      }

      if (!this.adjustmentCategory) {
        this.errors.category = 'Please select a category';
      }

      this.validateAmount(this.amount);
      this.validateReason(this.reason);

      if (this.selectedCategory?.requiresDocument && !this.supportingDocuments) {
        this.errors.documents = 'Supporting document reference is required for this category';
      }

      return Object.keys(this.errors).length === 0;
    },

    submitAdjustment() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      const data = new FormData();
      data.append("function", "createAdjustment");
      data.append("plotID", this.plot.id);
      data.append("plotNumber", this.plot.plotNumber);
      data.append("zone", this.plot.zone);
      data.append("ownerNames", this.plot.ownerNames || '');
      data.append("adjustmentType", this.adjustmentType);
      data.append("adjustmentCategory", this.adjustmentCategory);
      data.append("amount", this.amount.toString());
      data.append("reason", this.reason);
      data.append("fiscalYear", this.fiscalYear.toString());
      data.append("supportingDocuments", this.supportingDocuments || '');
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, plotRent)
        .then((res) => {
          this.isSubmitting = false;

          if (res.data.success) {
            const result = res.data.data;
            
            alert(`Adjustment Request Submitted!\n\n` +
                  `Reference: ${result.adjustmentRef}\n` +
                  `Amount: KES ${this.numberFormat(this.amount)}\n` +
                  `Type: ${this.adjustmentType}\n` +
                  `Required Approval: Level ${result.requiredApprovalLevel}\n\n` +
                  `The adjustment is now pending approval.`);

            this.$emit('adjustment-created', result);
            this.closeModal();
            this.resetForm();
          } else {
            alert("Failed: " + res.data.message);
          }
        })
        .catch((e) => {
          this.isSubmitting = false;
          alert("Error: " + e.message);
        });
    },

    resetForm() {
      this.adjustmentType = '';
      this.adjustmentCategory = '';
      this.amount = 0;
      this.reason = '';
      this.fiscalYear = new Date().getFullYear();
      this.supportingDocuments = '';
      this.errors = {};
    },

    closeModal() {
      try {
        const modal = document.querySelector('.adjustment-modal');
        if (modal) {
          const modalInstance = bootstrap.Modal.getInstance(modal);
          if (modalInstance) {
            modalInstance.hide();
          }
        }
      } catch (error) {
        console.error('Error closing modal:', error);
      }
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'Approved': return 'bg-success';
        case 'Rejected': return 'bg-danger';
        case 'Cancelled': return 'bg-secondary';
        case 'Pending': return 'bg-warning text-dark';
        case 'Pending Level 2': return 'bg-warning text-dark';
        case 'Pending Level 3': return 'bg-warning text-dark';
        default: return 'bg-info';
      }
    }
  }
}
</script>

<style scoped>
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.property-info,
.financial-info {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  height: 100%;
}

.adjustment-type-card {
  flex: 1;
  padding: 1rem;
  border: 2px solid #dee2e6;
  border-radius: 0.5rem;
  margin-right: 0;
  transition: all 0.2s ease;
  cursor: pointer;
}

.adjustment-type-card:hover {
  border-color: #6c757d;
  background-color: #f8f9fa;
}

.adjustment-type-card.selected {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
}

.adjustment-type-card:last-child.selected {
  border-color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.form-check-label {
  cursor: pointer;
  width: 100%;
}

.approval-level-badge .badge {
  font-size: 0.9rem;
}

.input-group-text {
  background-color: #f8f9fa;
  font-weight: 600;
}

.form-control:focus,
.form-select:focus {
  border-color: #ffc107;
  box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
}

@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-row .fw-bold {
    margin-left: 0 !important;
    margin-top: 0.25rem;
  }

  .d-flex.gap-3 {
    flex-direction: column;
  }

  .adjustment-type-card {
    margin-bottom: 0.5rem;
  }
}
</style>
